import React, { useState } from 'react';import API_BASE_URL from '../config';import styles from '../css-isolated/sa_add_cell.module.css';
import authFetch from '../utils/authFetch';

function SaAddCell({ isDarkMode, onSuccess, onError }) {
  const [formData, setFormData] = useState({
    typeOfCell: '',
    cellNo: '',
    capacity: '',
    note: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Type of Cell validation
    if (!formData.typeOfCell.trim()) {
      newErrors.typeOfCell = 'Type of cell is required';
    }

    // Cell Number validation
    if (!formData.cellNo.trim()) {
      newErrors.cellNo = 'Cell number is required';
    }

    // Capacity validation
    if (!formData.capacity) {
      newErrors.capacity = 'Capacity is required';
    } else if (parseInt(formData.capacity) < 1) {
      newErrors.capacity = 'Capacity must be at least 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await authFetch(`${API_BASE_URL}/api/cells`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type_of_cell: formData.typeOfCell,
          cell_no: formData.cellNo,
          capacity: parseInt(formData.capacity),
          current_occupancy: 0,
          note: formData.note || null
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle validation errors from Laravel
        if (data.errors) {
          const backendErrors = {};
          Object.keys(data.errors).forEach(key => {
            // Map Laravel field names to React field names
            const fieldMap = {
              'type_of_cell': 'typeOfCell',
              'cell_no': 'cellNo',
              'capacity': 'capacity',
              'note': 'note'
            };
            const reactKey = fieldMap[key] || key;
            backendErrors[reactKey] = data.errors[key][0];
          });
          setErrors(backendErrors);
          return;
        }
        throw new Error(data.message || 'Failed to add cell');
      }
      // Show success modal via callback with cell number
      if (onSuccess) {
        onSuccess(`Cell ${formData.cellNo} added successfully!`);
      }

      // Reset form on success
      handleReset();
    } catch (error) {
      console.error('Error adding cell:', error);
      if (onError) {
        onError(error.message || 'Failed to add cell. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      typeOfCell: '',
      cellNo: '',
      capacity: '',
      note: ''
    });
    setErrors({});
  };

  return (
    <div className={`${styles.contentWrapper} ${isDarkMode ? styles.darkMode : styles.lightMode}`}>
      <div className={styles.formSection}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionIcon}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="12" y1="8" x2="12" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div>
            <h2 className={styles.sectionTitle}>Cell Information</h2>
            <div className={styles.sectionAccent}></div>
          </div>
        </div>

        {errors.submit && (
          <div className={styles.errorAlert}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2"/>
              <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2"/>
            </svg>
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.formGrid}>
          <div className={styles.formRow}>
            <div className={styles.formField}>
              <label className={styles.formLabel}>
                Type of Cell <span className={styles.required}>*</span>
              </label>
              <select
                name="typeOfCell"
                value={formData.typeOfCell}
                onChange={handleInputChange}
                className={`${styles.formInput} ${errors.typeOfCell ? styles.error : ''}`}
              >
                <option value="">Select cell type</option>
                <option value="Isolation Cell">Isolation Cell</option>
                <option value="Male Cell">Male Cell</option>
                <option value="Female Cell">Female Cell</option>
              </select>
              {errors.typeOfCell && (
                <span className={styles.errorMessage}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2"/>
                    <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  {errors.typeOfCell}
                </span>
              )}
            </div>

            <div className={styles.formField}>
              <label className={styles.formLabel}>
                Cell Number <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                name="cellNo"
                value={formData.cellNo}
                onChange={handleInputChange}
                className={`${styles.formInput} ${errors.cellNo ? styles.error : ''}`}
                placeholder="Enter cell number (e.g., A-101, B-205)"
              />
              {errors.cellNo && (
                <span className={styles.errorMessage}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2"/>
                    <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  {errors.cellNo}
                </span>
              )}
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formField}>
              <label className={styles.formLabel}>
                Capacity <span className={styles.required}>*</span>
                <span className={styles.formLabelHint}>(Maximum occupants)</span>
              </label>
              <input
                type="number"
                name="capacity"
                value={formData.capacity}
                onChange={handleInputChange}
                className={`${styles.formInput} ${errors.capacity ? styles.error : ''}`}
                placeholder="Enter maximum capacity"
                min="1"
              />
              {errors.capacity && (
                <span className={styles.errorMessage}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2"/>
                    <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  {errors.capacity}
                </span>
              )}
            </div>
          </div>

          <div className={styles.formField}>
            <label className={styles.formLabel}>
              Notes
              <span className={styles.formLabelHint}>(Optional additional information)</span>
            </label>
            <textarea
              name="note"
              value={formData.note}
              onChange={handleInputChange}
              className={styles.formInput}
              placeholder="Enter any additional notes or remarks about this cell"
              rows="4"
            />
          </div>

          <div className={styles.submitButtonContainer}>
            <button
              type="button"
              onClick={handleReset}
              className={styles.resetButton}
              disabled={isLoading}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3 3v5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Reset
            </button>
            <button
              type="submit"
              className={`${styles.submitButton} ${isLoading ? styles.loading : ''}`}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className={styles.submitButtonText}>
                  <svg className={styles.loadingSpinner} width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeDasharray="32" strokeDashoffset="32">
                      <animate attributeName="stroke-dashoffset" dur="1s" repeatCount="indefinite" from="32" to="0"/>
                    </circle>
                  </svg>
                  Adding Cell...
                </span>
              ) : (
                <span className={styles.submitButtonText}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="17 21 17 13 7 13 7 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="7 3 7 8 15 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Add Cell
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SaAddCell;
